import type { TotvsApiDatasource } from '../datasources/totvs-api.datasource';
import type {
  CloseTotvsOperationalAlertInput,
  CreateTotvsProductLinkInput,
  ListTotvsOperationalAlertsInput,
  OperationalAlertFacet,
  TotvsOperationalAlertStatus,
  ListTotvsProductLinksInput,
  ListTotvsSyncTasksInput,
  Paginated,
  ProcessTotvsSyncTaskNowInput,
  ProcessTotvsSyncTaskNowOutput,
  ReprocessBatchTotvsSyncsInput,
  ReprocessBatchTotvsSyncsOutput,
  ReprocessTotvsSyncTaskInput,
  TotvsEntityEventsResponse,
  TotvsOperationalAlert,
  TotvsProductLink,
  TotvsProductLinkMatchInput,
  TotvsSyncTask,
  UpdateTotvsProductLinkInput,
  UpdateTotvsSyncTaskPayloadInput,
} from '../../domain/entities/totvs.entity';

export class TotvsRepositoryImpl {
  private api: TotvsApiDatasource;

  constructor(api: TotvsApiDatasource) {
    this.api = api;
  }

  listSyncTasks(
    input?: ListTotvsSyncTasksInput,
  ): Promise<Paginated<TotvsSyncTask>> {
    return this.api.listSyncTasks(input);
  }

  getSyncTask(id: string): Promise<TotvsSyncTask> {
    return this.api.getSyncTask(id);
  }

  reprocessSyncTask(
    id: string,
    input?: ReprocessTotvsSyncTaskInput,
  ): Promise<TotvsSyncTask> {
    return this.api.reprocessSyncTask(id, input);
  }

  processSyncTaskNow(
    id: string,
    input?: ProcessTotvsSyncTaskNowInput,
  ): Promise<ProcessTotvsSyncTaskNowOutput> {
    return this.api.processSyncTaskNow(id, input);
  }

  updateSyncTaskPayload(
    id: string,
    input: UpdateTotvsSyncTaskPayloadInput,
  ): Promise<TotvsSyncTask> {
    return this.api.updateSyncTaskPayload(id, input);
  }

  reprocessBatch(
    input: ReprocessBatchTotvsSyncsInput,
  ): Promise<ReprocessBatchTotvsSyncsOutput> {
    return this.api.reprocessBatch(input);
  }

  listProductLinks(
    input?: ListTotvsProductLinksInput,
  ): Promise<Paginated<TotvsProductLink>> {
    return this.api.listProductLinks(input);
  }

  getProductLinkMatch(
    input: TotvsProductLinkMatchInput,
  ): Promise<TotvsProductLink | null> {
    return this.api.getProductLinkMatch(input);
  }

  createProductLink(
    input: CreateTotvsProductLinkInput,
  ): Promise<TotvsProductLink> {
    return this.api.createProductLink(input);
  }

  updateProductLink(
    id: string,
    input: UpdateTotvsProductLinkInput,
  ): Promise<TotvsProductLink> {
    return this.api.updateProductLink(id, input);
  }

  deleteProductLink(id: string): Promise<TotvsProductLink> {
    return this.api.deleteProductLink(id);
  }

  listOperationalAlertFacets(
    status?: TotvsOperationalAlertStatus,
  ): Promise<OperationalAlertFacet[]> {
    return this.api.listOperationalAlertFacets(status);
  }

  listOperationalAlerts(
    input?: ListTotvsOperationalAlertsInput,
  ): Promise<Paginated<TotvsOperationalAlert>> {
    return this.api.listOperationalAlerts(input);
  }

  resolveOperationalAlert(
    id: string,
    input: CloseTotvsOperationalAlertInput,
  ): Promise<TotvsOperationalAlert> {
    return this.api.resolveOperationalAlert(id, input);
  }

  dismissOperationalAlert(
    id: string,
    input: CloseTotvsOperationalAlertInput,
  ): Promise<TotvsOperationalAlert> {
    return this.api.dismissOperationalAlert(id, input);
  }

  getEntityEvents(
    entityType: string,
    entityId: string,
    params: { page: number; perPage: number },
  ): Promise<TotvsEntityEventsResponse> {
    return this.api.getEntityEvents(entityType, entityId, params);
  }
}
